// The accounts app's frame: a dark top bar (company, + New journal) and the left menu.
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/lib/company";
import { fmtDate } from "@/lib/money";

const NAV = [
  { to: "/", label: "Dashboard", exact: true },
  { to: "/journals", label: "Journals", match: ["/journals"] },
  { to: "/reports", label: "Reports", match: ["/reports", "/ledger"] },
  { to: "/statements", label: "Statements" },
  { to: "/settlements", label: "Card settlements" },
  { to: "/station", label: "Station link" },
  { to: "/accounts", label: "Chart of accounts" },
  { to: "/setup", label: "Company setup" },
  { to: "/users", label: "Users", admin: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { companies, company, choose } = useCompany();
  const loc = useLocation();
  const navigate = useNavigate();
  const items = NAV.filter((n) => !n.admin || user?.role === "admin");
  const active = (n) => (n.exact ? loc.pathname === n.to : (n.match || [n.to]).some((m) => loc.pathname.startsWith(m)));
  return (
    <div className="min-h-screen bg-[#F6F5F3] text-[#1C1B1A]">
      <header className="flex flex-wrap items-center gap-4 bg-[#1C1B1A] px-6 py-3 text-white">
        <div className="flex min-w-[240px] flex-1 items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand font-bold" aria-hidden="true">{(company?.short || "A")[0]}</div>
          <div>
            {companies && companies.length > 1 ? (
              <select aria-label="Company" value={company?.id || ""} onChange={(e) => choose(e.target.value)}
                className="rounded border border-white/20 bg-transparent px-1 py-0.5 text-[15px] font-semibold">
                {companies.map((c) => <option key={c.id} value={c.id} className="text-black">{c.name}</option>)}
              </select>
            ) : <div className="text-[15px] font-semibold">{company?.name || "…"}</div>}
            <div className="text-xs text-[#C9C5BF]">{company ? `${(company.stations || []).join(", ")} · books from ${fmtDate(company.books_start)}` : ""}</div>
          </div>
        </div>
        <Link to="/journals/new" className="flex h-10 items-center rounded-full bg-brand px-5 text-sm font-semibold text-white hover:bg-brand/90">+ New journal</Link>
      </header>
      <div className="flex flex-wrap">
        <nav aria-label="Main" className="w-full border-b border-[#E4E2DE] bg-white px-3 py-3 md:min-h-[calc(100vh-64px)] md:w-[220px] md:border-b-0 md:border-r md:py-4">
          <div className="flex flex-wrap gap-1 md:flex-col md:gap-0.5">
            {items.map((n) => (
              <Link key={n.to} to={n.to} aria-current={active(n) ? "page" : undefined}
                className={`rounded-md px-3 py-2.5 text-sm ${active(n) ? "bg-[#F3E6E6] font-semibold text-brand" : "text-[#1C1B1A] hover:bg-[#F6F5F3]"}`}>
                {n.label}
              </Link>
            ))}
          </div>
          <div className="mt-6 hidden px-3 text-xs text-[#5F5C58] md:block">
            <div className="font-medium text-[#1C1B1A]">{user?.name || user?.email}</div>
            <div>{user?.role_label}</div>
            <button type="button" className="mt-3 text-brand hover:underline" onClick={async () => { await logout(); navigate("/login"); }}>Sign out</button>
          </div>
        </nav>
        <main className="min-w-0 flex-[999_1_560px]">{children}</main>
      </div>
    </div>
  );
}
